/**
 * ExecutiveSummary (CHAP-79) — ported from omega-club ExecutiveSummary.jsx onto
 * DB-backed `useTenantData`. Title + address + university + chapter + project
 * specs + executive narrative + a cabinet-only capital position.
 *
 * Mounted at /c/:slug/summary.
 */
import { useMemo } from 'react';
import { useParams } from 'react-router-dom';
import { useTenantData } from '../hooks/useTenantData.js';
import { TenantLoading, TenantError } from './TenantDataStates.js';
import { TierGuard } from './RoleGuard.js';
import { formatCurrency, formatNumber, formatPercent } from '../lib/formatters.js';

function parseNum(v: string | null | undefined): number {
  if (v == null) return 0;
  const n = Number(v);
  return Number.isFinite(n) ? n : 0;
}

export default function ExecutiveSummary() {
  const { slug } = useParams<{ slug: string }>();
  const { data, isLoading, error, refetch } = useTenantData(slug ?? '');

  const totals = useMemo(() => {
    if (!data) return null;
    const totalCapital = data.capitalStack.reduce((s, e) => s + (e.value ?? 0), 0);
    const totalSqft = data.llcEntities.reduce((s, e) => s + (e.sqft ?? 0), 0);
    const totalBeds = data.llcEntities.reduce((s, e) => s + (e.beds ?? 0), 0);
    const primary = data.financialModels.find((m) => m.isPrimary) ?? data.financialModels[0] ?? null;
    let year25Value = 0;
    let capRatePct = 0;
    if (primary) {
      const noi = primary.noi ?? 0;
      const growth = parseNum(primary.noiGrowthRate) / 100;
      capRatePct = parseNum(primary.capRate) || 6;
      year25Value = Math.round((noi * Math.pow(1 + growth, 25)) / (capRatePct / 100));
    }
    return { totalCapital, totalSqft, totalBeds, llcCount: data.llcEntities.length, primary, year25Value, capRatePct };
  }, [data]);

  if (isLoading) {
    return <TenantLoading />;
  }
  if (error) {
    return <TenantError message={String(error)} onRetry={() => refetch()} />;
  }
  if (!data || !totals) {
    return <div className="p-8 text-center text-sm text-neutral">No summary data for this chapter yet.</div>;
  }

  const t = data.tenant;
  const subtitle = [t.chapterOrg, t.university].filter(Boolean).join(' · ');

  return (
    <div className="space-y-4 sm:space-y-5 min-w-0">
      {/* Hero */}
      <div className="bg-navy rounded-xl shadow-sm p-4 sm:p-6 text-white min-w-0">
        <div className="flex items-start justify-between flex-wrap gap-3 min-w-0">
          <div className="min-w-0 flex-1">
            <h2 className="text-orange text-xl sm:text-2xl font-bold">{t.displayName}</h2>
            {subtitle && <p className="text-white/80 text-sm mt-1">{subtitle}</p>}
            {t.address && <p className="text-white/60 text-xs sm:text-sm mt-0.5">{t.address}</p>}
            <p className="text-white/70 text-xs mt-2">
              {formatNumber(totals.totalSqft)} sf · {totals.totalBeds} beds · {totals.llcCount} legal entit{totals.llcCount === 1 ? 'y' : 'ies'}
            </p>
          </div>
          <div className="flex flex-col items-end flex-shrink-0">
            <span className="text-[10px] uppercase tracking-wide text-white/60 font-bold">Total capital</span>
            <span className="text-orange text-2xl sm:text-3xl font-bold">{formatCurrency(totals.totalCapital)}</span>
          </div>
        </div>
      </div>

      {/* Project specs */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 min-w-0">
        <Stat icon={<CapitalIcon />} label="Total capital" value={formatCurrency(totals.totalCapital)} />
        <Stat icon={<AreaIcon />} label="Square feet" value={formatNumber(totals.totalSqft)} />
        <Stat icon={<BedIcon />} label="Beds" value={String(totals.totalBeds)} />
        <Stat icon={<EntityIcon />} label="Legal entities" value={String(totals.llcCount)} />
      </div>

      {/* Executive narrative */}
      <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-4 sm:p-6 min-w-0">
        <h3 className="text-navy font-semibold text-sm sm:text-base mb-2">Executive summary</h3>
        <p className="text-sm text-gray-700 leading-relaxed">
          {t.displayName}
          {subtitle ? ` (${subtitle})` : ''} is advancing a {formatCurrency(totals.totalCapital)} chapter-house capital project
          {t.address ? ` at ${t.address}` : ''}. The plan delivers {totals.totalBeds} beds across {formatNumber(totals.totalSqft)} square
          feet, structured through {totals.llcCount} legal entit{totals.llcCount === 1 ? 'y' : 'ies'}.
          {totals.primary && totals.year25Value > 0
            ? ` At a ${formatPercent(totals.capRatePct, 1)} cap rate, the adopted financial model projects a 25-year property value of ${formatCurrency(totals.year25Value)}.`
            : ''}
        </p>
      </div>

      {/* Cabinet-only capital position */}
      <TierGuard tier="cabinet">
        {totals.primary && (
          <div className="bg-white rounded-xl shadow-sm border border-orange/30 p-4 sm:p-6 min-w-0">
            <div className="flex items-center justify-between mb-3 flex-wrap gap-2">
              <h3 className="text-navy font-semibold text-sm sm:text-base">Capital position</h3>
              <span className="text-[10px] uppercase tracking-wider bg-orange/10 text-orange px-1.5 py-0.5 rounded font-semibold">Cabinet only</span>
            </div>
            <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 min-w-0">
              <Stat label="Loan amount" value={formatCurrency(totals.primary.loanAmount)} />
              <Stat label="Interest rate" value={formatPercent(parseNum(totals.primary.interestRate), 2)} />
              <Stat label="NOI (Yr 1)" value={formatCurrency(totals.primary.noi)} />
              <Stat label="Year-25 value" value={formatCurrency(totals.year25Value)} />
            </div>
          </div>
        )}
      </TierGuard>
    </div>
  );
}

function Stat({ icon, label, value }: { icon?: React.ReactNode; label: string; value: string }) {
  return (
    <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-3 sm:p-4 min-w-0">
      <div className="flex items-center gap-2 text-neutral">
        {icon}
        <span className="text-xs uppercase tracking-wider truncate">{label}</span>
      </div>
      <p className="text-navy font-bold text-lg sm:text-xl mt-1 truncate">{value}</p>
    </div>
  );
}

function CapitalIcon() {
  return (
    <svg className="w-4 h-4 flex-shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.5}>
      <path strokeLinecap="round" strokeLinejoin="round" d="M12 6v12m-3-2.818l.879.659c1.171.879 3.07.879 4.242 0 1.172-.879 1.172-2.303 0-3.182C13.536 12.219 12.768 12 12 12c-.725 0-1.45-.22-2.003-.659-1.106-.879-1.106-2.303 0-3.182s2.9-.879 4.006 0l.415.33M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
    </svg>
  );
}
function AreaIcon() {
  return (
    <svg className="w-4 h-4 flex-shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.5}>
      <path strokeLinecap="round" strokeLinejoin="round" d="M3.75 3.75v4.5m0-4.5h4.5m-4.5 0L9 9M3.75 20.25v-4.5m0 4.5h4.5m-4.5 0L9 15M20.25 3.75h-4.5m4.5 0v4.5m0-4.5L15 9m5.25 11.25h-4.5m4.5 0v-4.5m0 4.5L15 15" />
    </svg>
  );
}
function BedIcon() {
  return (
    <svg className="w-4 h-4 flex-shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.5}>
      <path strokeLinecap="round" strokeLinejoin="round" d="M3 12h18M3 12v6m18-6v6M3 18h18M5 12V8a2 2 0 012-2h10a2 2 0 012 2v4M8 10h3" />
    </svg>
  );
}
function EntityIcon() {
  return (
    <svg className="w-4 h-4 flex-shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.5}>
      <path strokeLinecap="round" strokeLinejoin="round" d="M2.25 21h19.5m-18-18v18m10.5-18v18m6-13.5V21M6.75 6.75h.75m-.75 3h.75m-.75 3h.75m3-6h.75m-.75 3h.75m-.75 3h.75" />
    </svg>
  );
}
